import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { toast as sonnerToast } from "sonner";
import { z } from "zod";
import { ModuleGuard } from "@/components/auth/ModuleGuard";
import ReportConfigDialog from "@/components/prospects/ReportConfigDialog";
import { supabase } from "@/integrations/supabase/client";
import { readReportSource, clearReportSource, type ReportSource } from "@/lib/reportSession";

const searchSchema = z.object({
  noteId: z.string().optional(),
  draftId: z.string().optional(),
});

export const Route = createFileRoute("/_app/prospects_/$id/report")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Compte-rendu IA — Thiia" },
      { name: "description", content: "Générez et modifiez le compte-rendu IA d'une note prospect." },
      { property: "og:title", content: "Compte-rendu IA — Thiia" },
      { property: "og:description", content: "Générez et modifiez le compte-rendu IA d'une note prospect." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { noteId, draftId } = Route.useSearch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : fr;
  const [source, setSource] = useState<ReportSource | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!noteId) { setReady(true); return; }
      const stored = readReportSource(noteId);
      if (stored) { setSource(stored); setReady(true); return; }
      // Fallback after a reload in another tab: rebuild from the note itself.
      const { data } = await supabase
        .from("prospect_notes" as any)
        .select("id, content, files, created_at")
        .eq("id", noteId)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const n = data as any;
        setSource({
          noteId: n.id,
          noteContent: n.content || "",
          noteFiles: Array.isArray(n.files) ? n.files : [],
          isVoiceNote: false,
          createdAt: n.created_at,
        });
      }
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [noteId]);

  const goBack = () => {
    navigate({ to: "/prospects/$id", params: { id }, search: { tab: "activite" } as any });
  };

  const handleSaveReport = async (content: string) => {
    if (!source) return;
    const { data: currentNote, error: fetchErr } = await supabase
      .from("prospect_notes" as any)
      .select("content")
      .eq("id", source.noteId)
      .single();
    if (fetchErr) { sonnerToast.error(fetchErr.message); throw fetchErr; }
    const separator = `\n\n---\n\n${t("prospects.notesUi.reportSeparator", { date: format(new Date(source.createdAt), "d MMM yyyy", { locale: dateLocale }) })}\n\n`;
    const updatedContent = ((currentNote as any)?.content || "").trimEnd() + separator + content;
    const { error } = await supabase
      .from("prospect_notes" as any)
      .update({ content: updatedContent } as any)
      .eq("id", source.noteId);
    if (error) { sonnerToast.error(error.message); throw error; }
    sonnerToast.success(t("prospects.notesUi.reportFinalized"));
  };

  return (
    <ModuleGuard module="prospects">
      <div className="h-full min-h-0 w-full">
        {ready && (
          <ReportConfigDialog
            open
            onOpenChange={(o) => {
              if (!o) {
                if (source) clearReportSource(source.noteId);
                goBack();
              }
            }}
            sourceNote={source}
            prospectId={id}
            onSaveReport={handleSaveReport}
            existingDraftId={draftId ?? null}
          />
        )}
      </div>
    </ModuleGuard>
  );
}
